// New Feedback Form Component
// Form for creating new feedback conversations with image attachment support
/* eslint-disable max-lines-per-function, complexity, max-len */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, ArrowLeft, Send, Paperclip } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ImageUploader } from './ImageUploader';
import type {
  FeedbackCategory,
  NewFeedbackFormData,
  FeedbackPriority,
  FeedbackAttachment
} from '../types';

interface NewFeedbackFormProps {
  categories: FeedbackCategory[];
  onSubmit: (data: NewFeedbackFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  showSuccessMessage?: boolean;
  tenantId?: string;
  userId?: string;
}

const priorityOptions: { value: FeedbackPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'text-gray-900 dark:text-gray-100' },
  { value: 'medium', label: 'Medium', color: 'text-blue-700 dark:text-blue-400' },
  { value: 'high', label: 'High', color: 'text-orange-700 dark:text-orange-400' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-700 dark:text-red-400' }
];

export const NewFeedbackForm: React.FC<NewFeedbackFormProps> = ({
  categories,
  onSubmit,
  onCancel,
  loading = false,
  showSuccessMessage = false,
  tenantId,
  userId
}) => {
  const [formData, setFormData] = useState<NewFeedbackFormData>({
    title: '',
    category_id: undefined,
    priority: 'medium',
    initial_message: '',
    attachments: []
  });

  const [errors, setErrors] = useState<Partial<Record<keyof NewFeedbackFormData, string>>>({});
  const [showAttachments, setShowAttachments] = useState(false);

  const handleInputChange = (field: keyof NewFeedbackFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAttachmentsChange = (attachments: FeedbackAttachment[]) => {
    setFormData(prev => ({ ...prev, attachments }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof NewFeedbackFormData, string>> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    }

    if (!formData.initial_message.trim()) {
      newErrors.initial_message = 'Message is required';
    } else if (formData.initial_message.trim().length < 10) {
      newErrors.initial_message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit({
        ...formData,
        title: formData.title.trim(),
        initial_message: formData.initial_message.trim(),
        attachments: formData.attachments
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  return (
    <div className='h-full flex flex-col'>
      <Card className='flex-1 flex flex-col min-h-0'>
        <CardHeader className='pb-4 flex-shrink-0'>
          <div className='flex items-center gap-3'>
            <Button variant='ghost' size='sm' onClick={onCancel} className='h-8 w-8 p-0'>
              <ArrowLeft className='h-4 w-4' />
            </Button>
            <CardTitle className='text-lg'>Create New Feedback</CardTitle>
          </div>
        </CardHeader>

        <CardContent className='flex-1 flex flex-col min-h-0'>
          {showSuccessMessage ? (
            <div className='flex-1 flex flex-col items-center justify-center text-center space-y-4 py-8'>
              <div className='w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center'>
                <Send className='h-8 w-8 text-green-700 dark:text-green-400' />
              </div>
              <div className='space-y-2'>
                <h3 className='text-xl font-semibold text-green-800 dark:text-green-300'>
                  Feedback Submitted!
                </h3>
                <p className='text-gray-900 dark:text-gray-100 max-w-md'>
                  Thank you for your feedback! Our product team has been notified and you can expect
                  to hear back within 24-48 hours.
                </p>
                <p className='text-sm text-gray-700 dark:text-gray-300'>
                  You can track your feedback status in the conversations list.
                </p>
              </div>
              <Button onClick={onCancel} variant='outline' className='mt-4'>
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className='flex-1 flex flex-col min-h-0'>
              <div className='flex-1 overflow-y-auto min-h-0 space-y-6 pr-2'>
                {/* Title */}
                <div className='space-y-2'>
                  <Label htmlFor='title' className='text-sm font-medium'>
                    Title *
                  </Label>
                  <Input
                    id='title'
                    value={formData.title}
                    onChange={e => handleInputChange('title', e.target.value)}
                    placeholder='Brief title for your feedback'
                    className={cn(errors.title && 'border-destructive')}
                    disabled={loading}
                  />
                  {errors.title && <p className='text-sm text-destructive'>{errors.title}</p>}
                </div>

                {/* Category */}
                <div className='space-y-2'>
                  <Label htmlFor='category' className='text-sm font-medium'>
                    Category{' '}
                    {categories.length === 0 && (
                      <span className='text-muted-foreground text-xs'>(loading...)</span>
                    )}
                  </Label>
                  <Select
                    value={formData.category_id || undefined}
                    onValueChange={value => handleInputChange('category_id', value)}
                    disabled={loading || categories.length === 0}
                  >
                    <SelectTrigger className={cn(errors.category_id && 'border-destructive')}>
                      <SelectValue
                        placeholder={
                          categories.length === 0 ? 'Loading categories...' : 'Select a category'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent position='popper' className='z-[10000]' sideOffset={4}>
                      {categories.length === 0 ? (
                        <SelectItem value='_loading' disabled>
                          Loading categories...
                        </SelectItem>
                      ) : (
                        categories.map(category => (
                          <SelectItem key={category.id} value={category.id}>
                            <div className='flex items-center gap-2'>
                              <div
                                className='w-3 h-3 rounded-full shrink-0'
                                style={{ backgroundColor: category.color }}
                              />
                              <span>{category.display_name}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {errors.category_id && (
                    <p className='text-sm text-destructive'>{errors.category_id}</p>
                  )}
                </div>

                {/* Priority */}
                <div className='space-y-2'>
                  <Label htmlFor='priority' className='text-sm font-medium'>
                    Priority
                  </Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value: FeedbackPriority) =>
                      handleInputChange('priority', value)
                    }
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select priority' />
                    </SelectTrigger>
                    <SelectContent position='popper' className='z-[10000]' sideOffset={4}>
                      {priorityOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className='flex items-center gap-2'>
                            <AlertTriangle className={cn('h-4 w-4', option.color)} />
                            <span className={cn('font-medium', option.color)}>{option.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Initial Message */}
                <div className='space-y-2 flex-1 flex flex-col'>
                  <Label htmlFor='initial_message' className='text-sm font-medium'>
                    Message *
                  </Label>
                  <Textarea
                    id='initial_message'
                    value={formData.initial_message}
                    onChange={e => handleInputChange('initial_message', e.target.value)}
                    placeholder='Describe your feedback in detail...'
                    className='flex-1 resize-none min-h-[100px]'
                    disabled={loading}
                  />
                  {errors.initial_message && (
                    <p className='text-sm text-destructive'>{errors.initial_message}</p>
                  )}
                </div>

                {/* Attachments Section */}
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <Label className='text-sm font-medium flex items-center gap-2'>
                      <Paperclip className='h-4 w-4' />
                      Attachments
                      <span className='text-muted-foreground font-normal'>(optional)</span>
                    </Label>
                    {!showAttachments && (
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={() => setShowAttachments(true)}
                        disabled={loading}
                        className='text-xs'
                      >
                        + Add files
                      </Button>
                    )}
                  </div>

                  {showAttachments && tenantId && userId && (
                    <ImageUploader
                      tenantId={tenantId}
                      userId={userId}
                      attachments={formData.attachments || []}
                      onAttachmentsChange={handleAttachmentsChange}
                      disabled={loading}
                      maxFiles={5}
                    />
                  )}

                  {showAttachments && (!tenantId || !userId) && (
                    <p className='text-sm text-muted-foreground'>
                      Unable to upload attachments. Please try again later.
                    </p>
                  )}

                  {(formData.attachments?.length ?? 0) > 0 && !showAttachments && (
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => setShowAttachments(true)}
                      className='w-full'
                    >
                      View {formData.attachments?.length} attachment
                      {(formData.attachments?.length ?? 0) !== 1 ? 's' : ''}
                    </Button>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className='flex-shrink-0 flex items-center justify-end gap-3 pt-4 border-t mt-6'>
                <Button type='button' variant='outline' onClick={onCancel} disabled={loading}>
                  Cancel
                </Button>

                <Button
                  type='submit'
                  disabled={loading || !formData.title.trim() || !formData.initial_message.trim()}
                  className='min-w-[120px]'
                >
                  {loading ? (
                    <>
                      <div className='h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent' />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Send className='h-4 w-4 mr-2' />
                      Create Feedback
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewFeedbackForm;
